import * as THREE from "three"
import { useEffect, useMemo, useRef } from "react"
import { useFrame } from "@react-three/fiber"
import { CuboidCollider, RigidBody } from "@react-three/rapier"
import { useSceneRef } from "../../../RefsProvider"
import { useTexture } from "@react-three/drei"
import InterestPoint from "./InterestPoint"
import useSfx from "../../../utils/useSfx"


const MAX_IMPACTS = 8
const FAR = 9999

const vertexShader = `
uniform vec3  uCenters[${MAX_IMPACTS}];
uniform float uForces[${MAX_IMPACTS}];

uniform float uTime;
uniform float uRadius;

uniform float uPush;
uniform float uLift;
uniform float uPointSize;

uniform float uNoiseScale;
uniform float uRadiusWarpAmp;
uniform float uWarpAmp;
uniform float uNoiseSpeed;

varying float vMask;
varying vec2 vUv;
varying vec2 vLocalXY;


float easeOutCubic(float x) {
  return 1.0 - pow(1.0 - x, 3.0);
}

float easeInOutQuad(float x) {
  if (x < 0.5)
    return 2.0 * x * x;
  return 1.0 - pow(-2.0 * x + 2.0, 2.0) / 2.0;
}

float hash31(vec3 p) {
  p = fract(p * 0.1031);
  p += dot(p, p.yzx + 33.33);
  return fract((p.x + p.y) * p.z);
}

float noise3(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  vec3 u = f * f * (3.0 - 2.0 * f);

  float n000 = hash31(i + vec3(0.0,0.0,0.0));
  float n100 = hash31(i + vec3(1.0,0.0,0.0));
  float n010 = hash31(i + vec3(0.0,1.0,0.0));
  float n110 = hash31(i + vec3(1.0,1.0,0.0));
  float n001 = hash31(i + vec3(0.0,0.0,1.0));
  float n101 = hash31(i + vec3(1.0,0.0,1.0));
  float n011 = hash31(i + vec3(0.0,1.0,1.0));
  float n111 = hash31(i + vec3(1.0,1.0,1.0));

  float n00 = mix(n000, n100, u.x);
  float n10 = mix(n010, n110, u.x);
  float n01 = mix(n001, n101, u.x);
  float n11 = mix(n011, n111, u.x);

  float n0 = mix(n00, n10, u.y);
  float n1 = mix(n01, n11, u.y);

  return mix(n0, n1, u.z);
}

void main() {
  vUv = uv;

  float best = 0.0;
  vec2 bestDelta = vec2(1.0, 0.0);

  // 1) WINNER pour calculer le mouvement (push/lift/warp)
  for (int i = 0; i < ${MAX_IMPACTS}; i++) {
    float f = uForces[i];
    if (f <= 0.0) continue;

    vec2 c = uCenters[i].xy;
    vec2 delta = position.xy - c;
    float d = length(delta);

    float n = noise3(vec3(position.xy * uNoiseScale, uTime * uNoiseSpeed));
    n = n * 2.0 - 1.0;

    float warpedRadius = uRadius + n * uRadiusWarpAmp;

    float m = smoothstep(warpedRadius, 0.0, d);
    m = pow(m, 0.9);
    m *= f;

    if (m > best) {
      best = m;
      bestDelta = delta;
    }
  }

  float bestRise = easeOutCubic(best);
  float bestFall = easeInOutQuad(best);
  float shaped = mix(bestFall, bestRise, best);

  vec3 pos = position;

  // mouvement radial + lift
  vec2 dir = normalize(bestDelta + vec2(1e-6));
  pos.xy += dir * (uPush * shaped);
  pos.z  += uLift * shaped;

  // warp 3D
  float nx = noise3(vec3(position.xy * uNoiseScale + 10.0, uTime * uNoiseSpeed));
  float ny = noise3(vec3(position.xy * uNoiseScale + 20.0, uTime * uNoiseSpeed));
  float nz = noise3(vec3(position.xy * uNoiseScale + 30.0, uTime * uNoiseSpeed));
  vec3 warp = vec3(nx, ny, nz) * 2.0 - 1.0;
  pos += warp * uWarpAmp * shaped;

  // 2) MASK FINAL basé sur la position DEFORMEE (pour l'alpha/contours)
  float best2 = 0.0;
  for (int i = 0; i < ${MAX_IMPACTS}; i++) {
    float f = uForces[i];
    if (f <= 0.0) continue;

    vec2 c = uCenters[i].xy;
    vec2 delta2 = pos.xy - c;
    float d2 = length(delta2);

    float n2 = noise3(vec3(pos.xy * uNoiseScale, uTime * uNoiseSpeed));
    n2 = n2 * 2.0 - 1.0;

    float warpedRadius2 = uRadius + n2 * uRadiusWarpAmp;

    float m2 = smoothstep(warpedRadius2, 0.0, d2);
    m2 = pow(m2, 0.9);
    m2 *= f;

    if (m2 > best2) best2 = m2;
  }

  vMask = best2;
  vLocalXY = pos.xy;

  vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  gl_PointSize = uPointSize;
}

`
const fragmentShader = `
uniform sampler2D uMap;
varying vec2 vUv;
varying float vMask;

void main() {
  vec2 p = gl_PointCoord.xy - 0.5;
  float r = length(p);
  if (r > 0.5) discard;

  vec3 tex = texture2D(uMap, vUv).rgb;

  // option : highlight dans la zone d'impact (opaque)
  vec3 hi = mix(tex, vec3(1.0), clamp(vMask, 0.0, 1.0) * 0.25);

  gl_FragColor = vec4(hi, 1.0);
}
`
const fragmentShader2 = `
  uniform sampler2D uMap;

  uniform vec3  uCenters[${MAX_IMPACTS}];
  uniform float uForces[${MAX_IMPACTS}];

  uniform float uTime;
  uniform float uRadiusMesh;
  uniform float uNoiseScale;
  uniform float uRadiusWarpAmp;
  uniform float uNoiseSpeed;

  varying vec2 vUv;
  varying vec2 vLocalXY;

  float hash31(vec3 p) {
  p = fract(p * 0.1031);
  p += dot(p, p.yzx + 33.33);
  return fract((p.x + p.y) * p.z);
}

float noise3(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  vec3 u = f * f * (3.0 - 2.0 * f);

  float n000 = hash31(i + vec3(0.0,0.0,0.0));
  float n100 = hash31(i + vec3(1.0,0.0,0.0));
  float n010 = hash31(i + vec3(0.0,1.0,0.0));
  float n110 = hash31(i + vec3(1.0,1.0,0.0));
  float n001 = hash31(i + vec3(0.0,0.0,1.0));
  float n101 = hash31(i + vec3(1.0,0.0,1.0));
  float n011 = hash31(i + vec3(0.0,1.0,1.0));
  float n111 = hash31(i + vec3(1.0,1.0,1.0));

  float n00 = mix(n000, n100, u.x);
  float n10 = mix(n010, n110, u.x);
  float n01 = mix(n001, n101, u.x);
  float n11 = mix(n011, n111, u.x);

  float n0 = mix(n00, n10, u.y);
  float n1 = mix(n01, n11, u.y);

  return mix(n0, n1, u.z);
}

  void main() {
    vec3 tex = texture2D(uMap, vUv).rgb;

    float best = 0.0;
    for (int i = 0; i < ${MAX_IMPACTS}; i++) {
      float f = uForces[i];
      if (f <= 0.0) continue;

      vec2 c = uCenters[i].xy;
      vec2 delta = vLocalXY - c;
      float d = length(delta);

      float n = noise3(vec3(vLocalXY * uNoiseScale, uTime * uNoiseSpeed));
      n = n * 2.0 - 1.0;

      float warpedRadius = uRadiusMesh + n * uRadiusWarpAmp;

      float m = smoothstep(warpedRadius, 0.0, d);
      m = pow(m, 0.9);
      m *= f;

      if (m > best) best = m;
    }

    // transparent dans la zone, opaque dehors
    float alpha = 1.0 - smoothstep(0.15, 0.85, clamp(best, 0.0, 1.0));

    gl_FragColor = vec4(tex, alpha);
  }

`

export default function Projects({ img, ...props }) {

  const map = useTexture(img)
  map.colorSpace = THREE.SRGBColorSpace
  map.wrapS = map.wrapT = THREE.ClampToEdgeWrapping

  const pointsRef = useRef()
  const refOvni = useSceneRef("ovni")
  const isTraversing = useRef(false)

  const tmpWorld = useMemo(() => new THREE.Vector3(), [])
  const tmpLocal = useMemo(() => new THREE.Vector3(), [])

  const PLANE_W = 10
  const PLANE_H = 6.5
  const HALF_X = PLANE_W * 0.5
  const HALF_Y = PLANE_H * 0.5

  const PADDING = 2.0
  const EXT_X = HALF_X + PADDING
  const EXT_Y = HALF_Y + PADDING

  const impactIndex = useRef(1)
  const lastStamp = useRef(new THREE.Vector3(FAR, FAR, FAR))


  const nano = useSfx("/audio/sfx/nano.mp3", true, 10, 10) // loop=true pour un bruit continu
  const lastOvniPos = useRef(new THREE.Vector3(FAR, FAR, FAR))


  const uniforms = useMemo(() => ({
    uCenters: { value: Array.from({ length: MAX_IMPACTS }, () => new THREE.Vector3(FAR, FAR, FAR)) },
    uForces: { value: new Float32Array(MAX_IMPACTS) },

    uRadius: { value: 3.0 },
    uRadiusMesh: { value: 5.2 },
    uTime: { value: 0 },

    uPush: { value: 1.2 },
    uLift: { value: 1.4 },
    uPointSize: { value: 4.0 },

    uNoiseScale: { value: 20.4 },
    uNoiseSpeed: { value: 0.2 },
    uRadiusWarpAmp: { value: 1.5 },
    uWarpAmp: { value: 0.4 },

    uMap: { value: map },
  }), [map])

  const pointsMaterial = useMemo(() => new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader: fragmentShader,
    uniforms,
    transparent: true,
    depthWrite: false,
  }), [uniforms])

  const meshMaterial = useMemo(() => new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader: fragmentShader2,
    uniforms,
    transparent: true,
    depthWrite: false,
  }), [uniforms])

  useFrame((state, delta) => {
    uniforms.uTime.value += delta

    const forces = uniforms.uForces.value
    const centers = uniforms.uCenters.value

    const fadeOut = 2.5
    const k = 6.0
    for (let i = 1; i < forces.length; i++) {
      forces[i] = Math.max(0, forces[i] - delta * fadeOut)
    }

    if (!isTraversing.current) {
      nano.targetVolume.current = 0

      forces[0] = THREE.MathUtils.damp(forces[0], 0.0, k, delta)
      if (forces[0] < 0.0001) centers[0].set(FAR, FAR, FAR)
      return
    }

    if (!refOvni.current || !pointsRef.current) return

    const t = refOvni.current.translation()
    tmpWorld.set(t.x, t.y, t.z)

    // --------- AUDIO (minimal) ---------
    if (lastOvniPos.current.x === FAR) lastOvniPos.current.copy(tmpWorld)

    const dist = tmpWorld.distanceTo(lastOvniPos.current)
    const speed = delta > 0 ? dist / delta : 0
    lastOvniPos.current.copy(tmpWorld)

    if (nano.loadedRef.current) {
      const minSpeed = 8
      const maxSpeed = 20

      const x = THREE.MathUtils.clamp((speed - minSpeed) / (maxSpeed - minSpeed), 0, 1)

      // volume : courbe qui donne moins de volume quand c’est lent
      const vol = x * x  // plus "au ralenti" (au lieu d'un smoothstep trop généreux)
      nano.targetVolume.current = 0.3 * vol

      // assure que ça joue tant que ça bouge un peu
      if (x > 0.001 && !nano.soundRef.current?.isPlaying) {
        nano.soundRef.current?.play(0)
      }

      // playback rate (si dispo)
      const s = nano.soundRef.current
      if (s && typeof s.setPlaybackRate === "function") {
        const rateMin = 0.65  // très lent
        const rateMax = 1.15  // un peu plus rapide
        const rate = THREE.MathUtils.lerp(rateMin, rateMax, x)
        s.setPlaybackRate(rate)
      }
    }

    // --------- /AUDIO ---------

    tmpLocal.copy(tmpWorld)
    pointsRef.current.worldToLocal(tmpLocal)

    tmpLocal.x = THREE.MathUtils.clamp(tmpLocal.x, -EXT_X, EXT_X)
    tmpLocal.y = THREE.MathUtils.clamp(tmpLocal.y, -EXT_Y, EXT_Y)


    if (isTraversing.current) {
      centers[0].copy(tmpLocal)
      forces[0] = THREE.MathUtils.damp(forces[0], 1.0, k, delta)
    } else {
      forces[0] = THREE.MathUtils.damp(forces[0], 0.0, k, delta)
      if (forces[0] < 0.0001) centers[0].set(FAR, FAR, FAR)
    }


    const minDist = 0.06
    if (tmpLocal.distanceTo(lastStamp.current) > minDist) {
      const idx = impactIndex.current

      centers[idx].copy(tmpLocal)
      forces[idx] = forces[0]

      impactIndex.current = idx + 1
      if (impactIndex.current >= forces.length) impactIndex.current = 1

      lastStamp.current.copy(tmpLocal)
    }

  })


  return (
    <group {...props}>
      <RigidBody type="fixed" colliders={false} rotation={[0, Math.PI * 1, 0]}>
        <points ref={pointsRef} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[PLANE_W, PLANE_H, 50, 50]} />
          <primitive object={pointsMaterial} attach="material" />
        </points>
        <mesh rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[PLANE_W, PLANE_H, 50, 50]} />
          <primitive object={meshMaterial} attach="material" />
        </mesh>

        <CuboidCollider
          sensor
          args={[EXT_X, 0.5, EXT_Y, 0.5]}
          onIntersectionEnter={() => {
            isTraversing.current = true

          }}
          onIntersectionExit={() => {
            isTraversing.current = false
          }}
        />
      </RigidBody>
    </group>
  )
}
